import React, { useRef, useState } from 'react';
import { MapPin } from 'lucide-react';
import { makeCommandId } from '../components/InvoiceApprovalReview';
import { setInvoiceSite } from './api';

/** How the server decided the site (backend/invoice_site.py SITE_SOURCES), in words. */
const SOURCE = {
  supplier_account: "the supplier account's site",
  uploader: 'the site of the person who uploaded it',
  intake: 'the site it was uploaded for',
  only_site: "the organisation's only site",
  person: 'chosen by a person',
  posted: 'where its stock was posted',
};

/**
 * The ONE site this invoice's stock goes to (PRD 14.2 rule 2), shown before approval. A manager may choose or change it
 * (only to a site they may use: `site.choices` is already narrowed by the server); the change is kept as a correction.
 */
export default function InvoiceSite({ invoice, site, canDecide = false, onChanged }) {
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const cmd = useRef(null);
  if (!site) return null;

  const editable = canDecide && site.can_change && (site.choices || []).length > 0;
  const save = async () => {
    if (!chosen) return;
    if (!cmd.current) cmd.current = makeCommandId('inv-site');
    setBusy(true);
    setError(null);
    try {
      await setInvoiceSite(invoice.id, { location_id: chosen, command_id: cmd.current });
      cmd.current = null;
      setChosen('');
      if (onChanged) await onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-2xl border border-surface-border bg-surface p-5 space-y-3" data-testid="invoice-site">
      {site.name ? (
        <p className="flex items-center gap-2 text-ink">
          <MapPin className="w-5 h-5 shrink-0 text-ink-muted" aria-hidden="true" />
          <span>
            Stock goes to <strong data-testid="invoice-site-name">{site.name}</strong>
            {SOURCE[site.source] && <span className="text-ink-muted"> ({SOURCE[site.source]})</span>}
          </span>
        </p>
      ) : null}
      {site.needs_choice && (
        <p className="rounded-xl bg-accent-50 border border-accent-300 px-4 py-3 text-accent-800" data-testid="invoice-site-choose">
          {canDecide
            ? "Choose the site this invoice's stock goes to. Nothing is posted until you do."
            : "A manager chooses the site this invoice's stock goes to before it is approved."}
        </p>
      )}
      {editable && (
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <label className="block flex-1">
            <span className="text-sm text-ink-muted">{site.name ? 'Change the site' : 'Site'}</span>
            <select value={chosen} onChange={(e) => setChosen(e.target.value)} data-testid="invoice-site-select"
              aria-label="Site this invoice's stock goes to"
              className="mt-1 w-full min-h-[44px] rounded-lg border border-surface-border bg-surface px-3 text-ink">
              <option value="">Choose a site…</option>
              {site.choices.filter((c) => c.id !== site.location_id).map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
          <button type="button" disabled={!chosen || busy} onClick={save} data-testid="invoice-site-save"
            className="inline-flex items-center justify-center min-h-[44px] px-5 rounded-xl font-medium bg-primary-500 text-surface hover:bg-primary-600 disabled:opacity-50">
            Save site
          </button>
        </div>
      )}
      {error && <p className="text-sm text-danger" data-testid="invoice-site-error">{error}</p>}
    </section>
  );
}
